import { ATOPIC_DERMATITIS_VIDEOS } from '../components/pages/modules/atopic-demermatitis/atopic-dermatitis.component';
import { CARDIOVASCULAR_DISEASE_VIDEOS } from '../components/pages/modules/cardiovascular-disease/cardiovascular-disease.component';
import { CARDIOVASCULAR_DISEASE_CHINA_VIDEOS } from '../components/pages/modules/cardiovascular-disease-china/cardiovascular-disease-china.component';
import { FLU_AND_VACCINATION_VIDEOS } from '../components/pages/modules/flu-and-vaccintation/flu-and-vaccination.component';
import { LIVER_DISEASE_VIDEOS } from '../components/pages/modules/liver-disease/liver-disease.component';
import { MENTAL_HEALTH_VIDEOS } from '../components/pages/modules/mental-health/mental-health.component';
import { OPHTHALMOLOGY_VIDEOS } from '../components/pages/modules/ophthalmology/ophthalmology.component';
import { PAIN_VIDEOS } from '../components/pages/modules/pain/pain.component';
import { RESPIRATORY_DISEASE_VIDEOS } from '../components/pages/modules/respiratory-disease/respiratory-disease.component';
import { SHARED_DECISION_MAKING_VIDEOS } from '../components/pages/modules/shared-decision-making/shared-decision-making.component';
import { UROLOGY_VIDEOS } from '../components/pages/modules/urology/urology.component';
import { PATIENT_STORY_VIDEOS } from '../components/pages/patient-messages/patient-messages.component';

export const ADMIN_PASSWORD = 'tqbfjotld';
export const ADMIN_SESSION_KEY = 'patient-academy-admin';

export interface CatalogEntry {
    category: string;
    topic: string;
    description: string;
    url: string;
}

export interface AdminVideoRow {
    category: string;
    topic: string;
    description: string;
    language: string;
    url: string;
}

export interface AdminVideoGroup {
    category: string;
    videos: AdminVideoRow[];
}

export interface VideoTranslations {
    en: Record<string, string>;
    es: Record<string, string>;
    cn: Record<string, string>;
}

const LANGUAGES: { code: keyof VideoTranslations; label: string }[] = [
    { code: 'en', label: 'English' },
    { code: 'es', label: 'Spanish' },
    { code: 'cn', label: 'Mandarin' },
];

export function isAdminPassword(value: string): boolean {
    return (value || '').trim() === ADMIN_PASSWORD;
}

export function videoFileName(video: { category: string; topic: string; language: string }): string {
    const raw = `${video.category} - ${video.topic} - ${video.language}`;
    const safe = raw.replace(/[<>:"/\\|?*\u0000-\u001f]/g, '').replace(/\s+/g, ' ').trim();
    return `${safe || 'video'}.mp4`;
}

function isDirectUrl(url: string): boolean {
    return /^https?:\/\//i.test(url);
}

function rowsForEntry(entry: CatalogEntry, translations: VideoTranslations): AdminVideoRow[] {
    if (isDirectUrl(entry.url)) {
        return [
            {
                category: entry.category,
                topic: entry.topic,
                description: entry.description,
                language: 'English',
                url: entry.url,
            },
        ];
    }

    const byUrl = new Map<string, string[]>();
    LANGUAGES.forEach((language) => {
        const translated = translations[language.code] && translations[language.code][entry.url];
        if (!translated || translated.indexOf('.mp4') === -1) {
            return;
        }
        const labels = byUrl.get(translated) || [];
        labels.push(language.label);
        byUrl.set(translated, labels);
    });

    const rows: AdminVideoRow[] = [];
    byUrl.forEach((labels, url) => {
        rows.push({
            category: entry.category,
            topic: entry.topic,
            description: entry.description,
            language: labels.join(', '),
            url,
        });
    });
    return rows;
}

export function groupCatalogEntries(entries: CatalogEntry[], translations: VideoTranslations): AdminVideoGroup[] {
    const groups: AdminVideoGroup[] = [];
    const indexByCategory = new Map<string, AdminVideoGroup>();

    entries.forEach((entry) => {
        const rows = rowsForEntry(entry, translations);
        if (!rows.length) {
            return;
        }
        let group = indexByCategory.get(entry.category);
        if (!group) {
            group = { category: entry.category, videos: [] };
            indexByCategory.set(entry.category, group);
            groups.push(group);
        }
        rows.forEach((row) => group.videos.push(row));
    });

    return groups;
}

function fromModule(category: string, videos: { topic: string; text?: string; url: string }[]): CatalogEntry[] {
    return videos.map((video) => ({
        category,
        topic: video.topic,
        description: video.text || '',
        url: video.url,
    }));
}

export function catalogEntries(): CatalogEntry[] {
    return [
        ...fromModule('Atopic Dermatitis', ATOPIC_DERMATITIS_VIDEOS),
        ...fromModule('Mental Health', MENTAL_HEALTH_VIDEOS),
        ...fromModule('Cardiovascular Disease', CARDIOVASCULAR_DISEASE_VIDEOS),
        ...fromModule('Respiratory', RESPIRATORY_DISEASE_VIDEOS),
        ...fromModule('Liver Disease', LIVER_DISEASE_VIDEOS),
        ...fromModule('Pain', PAIN_VIDEOS),
        ...fromModule('Ophthalmology', OPHTHALMOLOGY_VIDEOS),
        ...fromModule('Urology', UROLOGY_VIDEOS),
        ...fromModule('Shared decision making', SHARED_DECISION_MAKING_VIDEOS),
        ...fromModule('Flu and Vaccination', FLU_AND_VACCINATION_VIDEOS),
        ...PATIENT_STORY_VIDEOS.map((story) => ({
            category: 'Patient Stories',
            topic: story.title,
            description: story.description,
            url: story.src,
        })),
        {
            category: 'About',
            topic: 'Patient Academy teaser',
            description:
                'The vision of the Patient Academy is to create communities of empowered patients that take ownership in the management of their medical condition.',
            url: 'teaser-video',
        },
        ...fromModule('Cardiovascular Disease (China)', CARDIOVASCULAR_DISEASE_CHINA_VIDEOS),
    ];
}

export function buildAdminVideoGroups(translations: VideoTranslations): AdminVideoGroup[] {
    return groupCatalogEntries(catalogEntries(), translations);
}

export async function saveVideoFile(url: string, filename: string): Promise<void> {
    const savePicker = (window as unknown as { showSaveFilePicker?: (options: unknown) => Promise<any> }).showSaveFilePicker;

    if (typeof savePicker === 'function') {
        let handle: any;
        try {
            handle = await savePicker({
                suggestedName: filename,
                types: [{ description: 'MP4 video', accept: { 'video/mp4': ['.mp4'] } }],
            });
        } catch (error) {
            if (error && (error as { name?: string }).name === 'AbortError') {
                return;
            }
            return saveBlob(url, filename);
        }
        const response = await fetch(url);
        if (!response.ok || !response.body) {
            throw new Error('Download failed');
        }
        const writable = await handle.createWritable();
        await response.body.pipeTo(writable);
        return;
    }

    return saveBlob(url, filename);
}

async function saveBlob(url: string, filename: string): Promise<void> {
    const response = await fetch(url);
    if (!response.ok) {
        throw new Error('Download failed');
    }
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
}
